import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setSelectedRegionAndStoreFromStores } from 'store/requests/zone';

const { Option } = Select;

const SelectRegionsAndStores = (props) => {
  const { setSelectedRegionsAndStoresConfig, isDrawingEnabled } = props;
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const userStores = useSelector((state) => state?.definitions?.userStores);
  const activeRegion = useSelector(
    (state) => state.auth?.user?.activeRegion?.id
  );
  const activeStore = useSelector((state) => state.auth?.user?.activeStore?.id);
  const { t } = useTranslation();
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectAllRegions, setSelectAllRegions] = useState(false);
  const { selectedRegion, selectedStore, storesOfRegion } =
    useSelector((state) => state?.zone?.selectedStoreAndRegionFromStore) ??
    null;

  const getUniqueStores = (storesArray) => {
    const uniqueStores = [];
    const seenIds = new Set();

    storesArray.forEach((store) => {
      if (store && !seenIds.has(store.id)) {
        seenIds.add(store.id);
        uniqueStores.push(store);
      }
    });
    return uniqueStores;
  };

  useEffect(() => {
    return () => {
      setSelectedRegionAndStoreFromStores({
        selectedRegion: [],
        selectedStore: [],
        storesOfRegion: [],
      });
    };
  }, []);

  useEffect(() => {
    if (selectedRegion.length > 0) {
      setSelectedRegions(selectedRegion);
      setSelectedStores(selectedStore);
      const uniqueStores = getUniqueStores(storesOfRegion);
      setStores(uniqueStores);
      setSelectedRegionsAndStoresConfig({
        selectedRegions: [{ value: selectedRegion }],
        selectedStores: [{ value: selectedStore }],
        stores: uniqueStores,
        selectAllRegions: false,
      });
    } else {
      setLoading(true);
      if (userStores.length > 0 && userRegions.length > 0) {
        let allStoresSelectedRegions = [];

        if (activeRegion) {
          setSelectedRegions([{ value: activeRegion }]);
          userRegions.forEach((userRegion) => {
            if (userRegion.id === activeRegion) {
              allStoresSelectedRegions.push(...userRegion.children);
            }
          });
          const uniqueStores = getUniqueStores(allStoresSelectedRegions);
          setStores(uniqueStores);
          setSelectedRegionsAndStoresConfig({
            selectedRegions: [{ value: activeRegion }],
            selectedStores: [],
            stores: uniqueStores,
            selectAllRegions: false,
          });
        }
        if (activeStore) {
          const selectStore = userStores.find(
            (store) => store.id === activeStore
          );

          setSelectedRegions([{ value: selectStore?.region?.id }]);
          setSelectedStores([{ value: activeStore }]);
          userRegions.forEach((userRegion) => {
            if (userRegion.id === selectStore?.region?.id) {
              allStoresSelectedRegions.push(...userRegion.children);
            }
          });

          const uniqueStores = getUniqueStores(allStoresSelectedRegions);
          setStores(uniqueStores);
          setSelectedRegionsAndStoresConfig({
            selectedRegions: [{ value: selectStore?.region?.id }],
            selectedStores: [{ value: activeStore }],
            stores: uniqueStores,
            selectAllRegions: false,
          });
        }
      } else if (userStores.length > 0) {
        const store = userStores.map((store) => ({
          id: store.id,
          title: store.name,
          code: store.code,
        }));

        const uniqueStores = getUniqueStores(store);
        setStores(uniqueStores);
        setSelectedStores([{ value: activeStore }]);
        setSelectedRegionsAndStoresConfig({
          selectedRegions: [],
          selectedStores: activeStore ? [{ value: activeStore }] : [],
          stores: uniqueStores,
          selectAllRegions: false,
        });
      }
      setLoading(false);
    }
  }, [
    userStores,
    userRegions,
    activeRegion,
    activeStore,
    selectedRegion,
    selectedStore,
  ]);

  const handleChangeRegion = async (selected, options) => {
    let allStoresSelectedRegions = [];
    const selectAllRegions = options.find((option) => option.key === 'all');
    setSelectedRegions([]);
    setSelectedStores([]);

    if (selectAllRegions) {
      setSelectAllRegions(true);
      userRegions.forEach((userRegion) => {
        const storesOfRegion = userRegion.children || [];
        allStoresSelectedRegions.push(...storesOfRegion);
      });
      setSelectedRegions([{ value: 'all' }]);
      const uniqueStores = getUniqueStores(allStoresSelectedRegions);
      setStores(uniqueStores);

      setSelectedRegionsAndStoresConfig({
        selectedRegions: [{ value: 'all' }],
        selectedStores: [],
        stores: uniqueStores,
        selectAllRegions: true,
      });
    } else {
      setSelectedRegions(selected);
      setSelectAllRegions(false);
      selected.forEach((selectedRegion) => {
        const storesOfRegion =
          userRegions.find((region) => region.id === selectedRegion.value)
            ?.children || [];
        allStoresSelectedRegions.push(...storesOfRegion);
      });
      const uniqueStores = getUniqueStores(allStoresSelectedRegions);
      setStores(uniqueStores);
      setSelectedRegionsAndStoresConfig({
        selectedRegions: selected,
        selectedStores: [],
        stores: uniqueStores,
        selectAllRegions: false,
      });
    }
  };

  const handleChangeStore = (selected) => {
    setSelectedStores(selected);
    setSelectedRegionsAndStoresConfig((prev) => ({
      ...prev,
      selectedRegions: selectedRegions,
      selectedStores: selected,
      selectAllRegions: selectAllRegions,
    }));
  };

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        marginBottom: '10px',
      }}
    >
      {isDrawingEnabled && (
        <Select
          className="fullWidth"
          mode="multiple"
          showSearch
          filterOption={true}
          optionFilterProp="children"
          placeholder={t('selectRegion')}
          style={{ width: '50%', marginRight: '2px' }}
          onChange={handleChangeRegion}
          maxTagCount={5}
          labelInValue
          value={selectedRegions}
        >
          <Option key={'all'} value={'all'}>
            {t('selectAll')}
          </Option>
          {!selectAllRegions &&
            userRegions
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((region) => (
                <Option key={region.id} value={region.id}>
                  {region.title}
                </Option>
              ))}
        </Select>
      )}
      <Select
        className="fullWidth"
        showSearch
        filterOption={true}
        optionFilterProp="children"
        placeholder={t('selectStore')}
        style={{ width: '50%', marginRight: '2px' }}
        onChange={handleChangeStore}
        mode="multiple"
        maxTagCount={5}
        value={selectedStores}
      >
        {stores &&
          stores.map((store) => (
            <Option key={store.id} value={store.id}>
              {store.title}
            </Option>
          ))}
      </Select>
    </div>
  );
};

export default SelectRegionsAndStores;
